// adminfrontend/src/admin_ui/pages/AdminStockLevelsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function AdminStockLevelsPage({ adminEmail, onLogout }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");

  const [productId, setProductId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectedBranch = useMemo(
    () => branches.find((b) => String(b.id) === String(branchId)) || null,
    [branches, branchId]
  );

  useEffect(() => {
    const qBranchId = searchParams.get("branchId");
    const qLocationId = searchParams.get("locationId");
    const qProductId = searchParams.get("productId");
    const qBatchId = searchParams.get("batchId");
    if (qBranchId) setBranchId(String(qBranchId).replace(/[^\d]/g, ""));
    if (qLocationId) setLocationId(String(qLocationId).replace(/[^\d]/g, ""));
    if (qProductId) setProductId(String(qProductId).replace(/[^\d]/g, ""));
    if (qBatchId) setBatchId(String(qBatchId).replace(/[^\d]/g, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadBranches() {
    const data = await adminInventoryApi.listBranches();
    if (!data || data.ok !== true) throw new Error("branches_failed");
    const bs = data.branches || [];
    setBranches(bs);
    if (!branchId && bs[0]) setBranchId(String(bs[0].id));
    return bs;
  }

  async function loadLocations(bId) {
    if (!bId) {
      setLocations([]);
      return;
    }
    const data = await adminInventoryApi.listLocations(bId);
    if (!data || data.ok !== true) throw new Error("locations_failed");
    setLocations(data.locations || []);
  }

  async function loadStock() {
    if (!branchId) return;
    setLoading(true);
    setError("");

    try {
      const params = {
        branchId: Number(branchId),
        limit,
        offset,
      };
      if (locationId) params.locationId = Number(locationId);
      if (productId) params.productId = Number(productId);
      if (batchId) params.batchId = Number(batchId);

      const data = await adminInventoryApi.listStockLevels(params);
      if (!data || data.ok !== true) throw new Error("stock_failed");

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      console.error("[AdminStockLevelsPage] load error:", e);
      setError("Unable to load stock levels. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function fullInitLoad() {
    setLoading(true);
    setError("");
    try {
      const bs = await loadBranches();
      const effectiveBranchId = branchId || (bs[0] ? String(bs[0].id) : "");
      if (effectiveBranchId) await loadLocations(effectiveBranchId);
    } catch (e) {
      console.error("[AdminStockLevelsPage] init error:", e);
      setError("Unable to load branches/locations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fullInitLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocationId("");
    setOffset(0);
    loadLocations(branchId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, locationId, productId, batchId, limit, offset]);

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  function goToMovementHistory(row) {
    const qs = new URLSearchParams();
    if (branchId) qs.set("branchId", String(branchId));
    if (row?.location_id) qs.set("locationId", String(row.location_id));
    if (row?.product_id) qs.set("productId", String(row.product_id));
    if (row?.batch_id) qs.set("batchId", String(row.batch_id));
    navigate(`/stock-movements?${qs.toString()}`);
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Stock Levels</h1>
            <p className="ucx-admin-subtitle">
              Read-only view of current stock by branch/location, including batch-level rows.
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}

            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={loadStock}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button
                type="button"
                className="ucx-admin-btn ucx-admin-btn-reject"
                onClick={onLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>

          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ""}
                </option>
              ))}
            </select>

            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} {l.code ? `(${l.code})` : ""} [{l.type}]
                </option>
              ))}
            </select>

            <input
              type="text"
              value={productId}
              onChange={(e) => {
                setOffset(0);
                setProductId(e.target.value.replace(/[^\d]/g, ""));
              }}
              placeholder="Product ID (optional)"
            />

            <input
              type="text"
              value={batchId}
              onChange={(e) => {
                setOffset(0);
                setBatchId(e.target.value.replace(/[^\d]/g, ""));
              }}
              placeholder="Batch ID (optional)"
            />

            <select
              value={limit}
              onChange={(e) => {
                setOffset(0);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          <div className="ucx-admin-toolbar">
            <div className="ucx-admin-toolbar-hint">
              Branch: <b>{selectedBranch ? selectedBranch.name : "—"}</b>
            </div>

            <div className="ucx-admin-pager">
              <span className="ucx-small">
                Showing{" "}
                <b>{total === 0 ? 0 : Math.min(total, offset + 1)}</b>–<b>{Math.min(total, offset + limit)}</b>{" "}
                of <b>{total}</b>
              </span>

              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                disabled={!canPrev || loading}
              >
                Prev
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => setOffset((o) => o + limit)}
                disabled={!canNext || loading}
              >
                Next
              </button>
            </div>
          </div>

          {!!error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>StockLevel ID</th>
                <th>Product</th>
                <th>Batch</th>
                <th>Location</th>
                <th>Qty</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    {r.product_id}
                    {r.product_name ? ` — ${r.product_name}` : ""}
                  </td>
                  <td>{r.batch_id ? `${r.batch_id} (${r.ucx_batch || "-"})` : "-"}</td>
                  <td>
                    {r.location_name} {r.location_code ? `(${r.location_code})` : ""} [{r.location_type}]
                  </td>
                  <td>{r.quantity}</td>
                  <td>{r.updated_at ? new Date(r.updated_at).toLocaleString() : "-"}</td>
                  <td>
                    <button
                      type="button"
                      className="ucx-btn ucx-btn-outline"
                      onClick={() => goToMovementHistory(r)}
                      disabled={!branchId}
                      title="Open movement history filtered to this row"
                    >
                      Movement history
                    </button>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    Loading stock levels…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    No stock levels found for these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminStockLevelsPage;
